import { useState, useEffect, useRef, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { MagneticButton } from "@/components/ui/magnetic-button";
import { Menu, X } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useActiveSection } from "@/hooks/use-active-section";
import LanguageSwitcher from "./LanguageSwitcher";
import { cn } from "@/lib/utils";
const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const [isAtTop, setIsAtTop] = useState(true);
  const { t } = useLanguage();
  
  const lastScrollY = useRef(0);
  const ticking = useRef(false);

  const navLinks = useMemo(() => [
    { href: "#shows", id: "shows", label: t.nav.shows },
    { href: "#about", id: "about", label: t.nav.about },
    { href: "#gallery", id: "gallery", label: t.nav.gallery },
    { href: "#my-tickets", id: "my-tickets", label: t.nav.myTickets },
    { href: "#contact", id: "contact", label: t.nav.contact },
  ], [t]);

  const sectionIds = useMemo(() => navLinks.map(link => link.id), [navLinks]);
  const activeSection = useActiveSection(sectionIds, 150);

  useEffect(() => {
    const handleScroll = () => {
      if (!ticking.current) {
        window.requestAnimationFrame(() => {
          const currentScrollY = window.scrollY;
          
          // Check if at top
          setIsAtTop(currentScrollY < 10);
          
          // Only apply hide/show logic on mobile and when menu is closed
          if (window.innerWidth < 768 && !isOpen) {
            // Scrolling down - hide navbar (only after scrolling past 100px)
            if (currentScrollY > lastScrollY.current && currentScrollY > 100) {
              setIsVisible(false);
            }
            // Scrolling up - show navbar
            else if (currentScrollY < lastScrollY.current) {
              setIsVisible(true);
            }
          } else {
            setIsVisible(true);
          }
          
          lastScrollY.current = currentScrollY;
          ticking.current = false;
        });
        ticking.current = true;
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [isOpen]);

  return (
    <nav 
      className={cn(
        "fixed top-0 left-0 right-0 z-50 border-b transition-all duration-500",
        isAtTop 
          ? "bg-transparent border-transparent" 
          : "bg-background/80 backdrop-blur-xl border-border/30 shadow-lg shadow-background/20",
        isVisible ? "translate-y-0" : "-translate-y-full"
      )}
    >
      <div className="container mx-auto px-4 md:px-6 py-3 md:py-4">
        <div className="flex items-center justify-between">
          <a href="#" className="flex items-center gap-3 group">
            <img
              alt="Jazz Insights Plovdiv"
              className="h-20 md:h-24 w-auto opacity-100 object-contain transition-transform duration-300 group-hover:scale-105"
              src="/lovable-uploads/c53fa286-eb95-47d0-bd04-e67a728fcf07.png"
            />
          </a>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => {
              const isActive = activeSection === link.id;
              return (
                <a
                  key={link.href}
                  href={link.href}
                  className={cn(
                    "relative py-1 font-body tracking-wide transition-colors duration-300",
                    "after:absolute after:bottom-0 after:left-0 after:h-[2px] after:bg-gold after:transition-all after:duration-300",
                    isActive 
                      ? "text-gold after:w-full" 
                      : "text-foreground/80 hover:text-gold after:w-0 hover:after:w-full"
                  )}
                >
                  {link.label}
                </a>
              );
            })}
            <LanguageSwitcher />
            <MagneticButton
              variant="gold"
              size="sm"
              strength={0.4}
              radius={100}
              asChild
            >
              <Link to="/tickets">
                {t.nav.buyTickets}
              </Link>
            </MagneticButton>
          </div>

          {/* Mobile: Language + Menu */}
          <div className="md:hidden flex items-center gap-2">
            <LanguageSwitcher />
            <button
              className="text-foreground"
              onClick={() => setIsOpen(!isOpen)}
              aria-label="Toggle menu"
            >
              {isOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div className="md:hidden absolute left-0 right-0 top-full bg-background/95 backdrop-blur-xl border-b border-border/50 shadow-lg animate-slide-down">
            <div className="container mx-auto px-6 py-6 flex flex-col gap-4">
              {navLinks.map((link) => {
                const isActive = activeSection === link.id;
                return (
                  <a
                    key={link.href}
                    href={link.href}
                    className={cn(
                      "font-body transition-colors duration-300 py-2",
                      isActive ? "text-gold" : "text-foreground/80 hover:text-gold"
                    )}
                    onClick={() => setIsOpen(false)}
                  >
                    {link.label}
                  </a>
                );
              })}
              <div className="py-2">
                <LanguageSwitcher />
              </div>
              <Button
                variant="gold"
                size="sm"
                className="w-fit"
                asChild
                onClick={() => setIsOpen(false)}
              >
                <Link to="/tickets">
                  {t.nav.buyTickets}
                </Link>
              </Button>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
